import type { FeatureCollection } from "geojson";
import type { GeoJSONSource, Map } from "mapbox-gl";
import type { GeoJsonFeature, AssignmentFeatureProperties } from "../api/map/types";
import { ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID } from "./adminAssignmentMapHighlight";

export const ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID = "admin-assignments-overview";
export const ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID = "admin-assignments-overview-fill";
export const ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID = "admin-assignments-overview-line";

/** Blue tones read clearly on neutral OSM greys without competing with the green “Visa” highlight. */
const OVERVIEW_FILL_COLOR = "#2563eb";
const OVERVIEW_FILL_OPACITY = 0.16;
const OVERVIEW_LINE_COLOR = "#1d4ed8";
const OVERVIEW_LINE_WIDTH = 1.5;
const OVERVIEW_LINE_OPACITY = 0.62;

const applyOverviewPaint = (map: Map): void => {
    if (!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID)) {
        return;
    }
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, "fill-color", OVERVIEW_FILL_COLOR);
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, "fill-opacity", OVERVIEW_FILL_OPACITY);
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "line-color", OVERVIEW_LINE_COLOR);
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "line-width", OVERVIEW_LINE_WIDTH);
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "line-opacity", OVERVIEW_LINE_OPACITY);
};

const getBeforeLayerId = (map: Map): string | undefined =>
    map.getStyle()?.layers?.find((l) => l.type === "symbol")?.id;

const ensureOverviewLayers = (map: Map): void => {
    const beforeId = getBeforeLayerId(map);

    if (!map.getSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID)) {
        map.addSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] }
        });
    }

    if (!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID)) {
        map.addLayer({
            id: ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID,
            type: "fill",
            source: ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID,
            paint: {
                "fill-color": OVERVIEW_FILL_COLOR,
                "fill-opacity": OVERVIEW_FILL_OPACITY
            }
        }, beforeId);
    }

    if (!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID)) {
        map.addLayer({
            id: ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID,
            type: "line",
            source: ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID,
            paint: {
                "line-color": OVERVIEW_LINE_COLOR,
                "line-width": OVERVIEW_LINE_WIDTH,
                "line-opacity": OVERVIEW_LINE_OPACITY
            }
        }, beforeId);
    }
};

const stackOverviewBelowHighlight = (map: Map): void => {
    if (!map.getLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID)) {
        return;
    }
    if (map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID)) {
        map.moveLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID);
    }
    if (map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID)) {
        map.moveLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID);
    }
};

/**
 * Faint assignment polygons on admin basemap. Hidden while drawing so TerraDraw is unobstructed.
 */
export function updateAdminAssignmentsOverviewLayer(
    map: Map,
    features: GeoJsonFeature<AssignmentFeatureProperties>[],
    options: { show: boolean }
): void {
    const { show } = options;
    if (!map.isStyleLoaded()) {
        map.once("style.load", () => updateAdminAssignmentsOverviewLayer(map, features, options));
        return;
    }

    const hasLayers =
        !!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID) &&
        !!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID);

    if (!show) {
        if (hasLayers) {
            map.setLayoutProperty(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, "visibility", "none");
            map.setLayoutProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "visibility", "none");
        }
        return;
    }

    if (features.length === 0) {
        removeAdminAssignmentsOverviewFromMap(map);
        return;
    }

    ensureOverviewLayers(map);
    applyOverviewPaint(map);

    const source = map.getSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID) as GeoJSONSource | undefined;
    if (!source) {
        return;
    }

    const data: FeatureCollection = {
        type: "FeatureCollection",
        features: features as FeatureCollection["features"]
    };
    source.setData(data);

    map.setLayoutProperty(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, "visibility", "visible");
    map.setLayoutProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "visibility", "visible");

    stackOverviewBelowHighlight(map);
}

export function removeAdminAssignmentsOverviewFromMap(map: Map): void {
    try {
        if (map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID)) {
            map.removeLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID);
        }
        if (map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID)) {
            map.removeLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID);
        }
        if (map.getSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID)) {
            map.removeSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID);
        }
    } catch {
        // style reload race
    }
}
